import { mergeDeepRight } from 'ramda'
import { DEFAULT_PAYLOAD, SENDING_SKIPPED } from '@/lib/analytics/consts.ts'
import { useAnalytics } from '@/composables/analytics'

export default class AnalyticsHandler {
  constructor (config, manager) {
    const DELAYS = [1, 100, 250, 500, 1000, 2000, 3000, 5000]

    /* handler name */
    this.name = config.name

    /* handler code */
    this.code = config.code

    /* configuration */
    this.config = config

    /* manager */
    this.manager = manager

    /* logger */
    this.logger = this.manager.logger

    /* event params converters */
    this.eventConverters = config.eventConverters

    /* is initialized */
    this.initialized = false

    /* events functions queue */
    this.queue = []

    /*! sending skipped */
    this.sendingSkipped = SENDING_SKIPPED

    /* analytics composable */
    this.analytics = useAnalytics()

    /*
     * calls `setTimeout` n-times; stops when
     * `__initialize` returns `true`
     */
    this.__nTimeouts(
      () => { return this.__initialize() },
      DELAYS,
      () => { this.initializationError('not set (timeout error)') }
    )
  }

  __initialize () {
    this.initialized = true

    this.debug(`AnalyticsHandler registered`)

    while (this.queue.length) {
      const { eventFunction, defaultPayload } = this.queue.shift()
      this.__sendEvent(eventFunction, defaultPayload)
    }

    return true
  }

  dispatch (data) {
    const eventFunction = this.__prepareEventFunction(data)

    if (eventFunction === false) {
      return false
    }

    const defaultPayload = this.__getDefaultPayload(data)

    if (this.initialized) {
      this.__sendEvent(eventFunction, defaultPayload)
    } else {
      this.queue.push({ eventFunction, defaultPayload })
    }
  }

  __getDefaultPayload (data) {
    const method = this[DEFAULT_PAYLOAD]

    if (typeof method !== 'function') {
      return null
    }

    try {
      const payload = this[DEFAULT_PAYLOAD](data)
      return payload === this.sendingSkipped ? null : payload
    } catch (e) {
      this.error('An error occured during __getDefaultPayload method', e)
      return null
    }
  }

  __prepareEventFunction (data) {
    const { name } = data
    let handlerName = `event${name}Handler`

    if (typeof this[handlerName] === 'function') {
      return this[handlerName].bind(this, data)
    }

    if (typeof this[name] === 'function') {
      return this[name].bind(this, data)
    }

    return false
  }

  __sendEvent (eventFunction, defaultPayload) {
    try {
      /*! get event function result */
      const event = this.__prepareEvent(eventFunction)
      const list = Array.isArray(event) ? event : [event]
      list.forEach((item) => {
        this.__sendEventItem(item, defaultPayload)
      })
    } catch (e) {
      this.error('An error occured during __sendEvent method', e)
    }
  }

  __prepareEvent (eventFunction) {
    try {
      return eventFunction()
    } catch (e) {
      const functionName = this.__getFunctionName(eventFunction)
      this.error(`invalid executon of ${functionName} from ${this.name}.__prepareEvent`, e)
      /*! don't send event which cannot be prepared */
      return this.sendingSkipped
    }
  }

  __sendEventItem (e, defaultPayload) {
    const isPureObject = this.__isPureObject(e)
    const isPromise = e && typeof e.then === 'function'
    const errorMessage = `invalid response returned from ${this.name}.__prepare`

    if (e === this.sendingSkipped) {
      return
    }

    if (isPureObject) {
      this.__tryToSend(mergeDeepRight(defaultPayload, e))
      return
    }

    if (isPromise) {
      e.then(payload => {
        if (payload === this.sendingSkipped) {
          return
        }

        if (this.__isPureObject(payload)) {
          this.__tryToSend(mergeDeepRight(defaultPayload, payload))
        } else {
          this.error(errorMessage, payload)
        }
      }).catch((e) => {
        this.error(errorMessage, e)
      })
      return
    }

    this.error(errorMessage, e)
  }

  __tryToSend (event) {
    if (Array.isArray(this.eventConverters)) {
      this.eventConverters.forEach(converter => {
        event = this.__tryToConvertEvent({ converter, event })
      })
    }

    try {
      this.send(event)
      this.__sendDebugEvent(event)
    } catch (e) {
      this.error('An error occured during send method', e)
    }
  }

  __tryToConvertEvent ({ converter, event }) {
    try {
      event = converter(event)
    } catch (e) {
      this.error('An error occured during event params conversion', e)
    }

    return event
  }

  __sendDebugEvent (event) {
    const debugEvent = new CustomEvent('analytics:debug', {
      detail: {
        event: {
          handler: this.name,
          data: event,
        },
      },
    })

    document.dispatchEvent(debugEvent)
  }

  send () {
    this.error(`${this.name}.send not implemented!`)
  }

  error () {
    this.logger.error(this.name, ...arguments)
  }

  debug () {
    this.logger.debug(this.name, ...arguments)
  }

  initializationError (message) {
    this.error(message)
    this.manager.removeHandler(this)
  }

  __nTimeouts (fn, timeouts, failureCallback) {
    if (Array.isArray(timeouts) && timeouts.length) {
      const timeout = timeouts.shift()

      window.setTimeout(() => {
        Promise.resolve(fn())
          .then(val => {
            if (!val) {
              this.__nTimeouts(fn, timeouts, failureCallback)
            }
          })
      }, timeout)
    } else if (typeof failureCallback === 'function') {
      failureCallback()
    }
  }

  // By default we are blocking sending events to extrenal tools like GTM or Segment
  // To avoid polluting data with test events in databases
  // If needed, this can be overriden by passing send_analytics=1 query parameter in URL on test env

  // event, handlerStack and handlerMethod are used for debugging purposes
  sendToExternalToolIfAllowed (sendEventCallback, event, handlerStack, handlerMethod = '') {
    const sendingAllowed = this.analytics.shouldSendEventsExternally.value

    if (sendingAllowed) {
      sendEventCallback()
      this.debug(`${this.config.code} ${handlerMethod} sent:`, { event, handlerStack })
    } else {
      this.debug(`${this.config.code} ${handlerMethod} triggered, but blocked on test env:`, { event, handlerStack })
    }

    return false
  }

  __isPureObject (obj) {
    return obj && obj.constructor && obj.constructor.name === 'Object'
  }

  __getFunctionName (eventFunction) {
    let functionName = eventFunction.name

    if (functionName.indexOf('bound ') === 0) {
      return functionName.slice('bound '.length)
    }

    return functionName
  }

  isProduction () {
    return window.location.hostname === 'booksy.com'
  }
}
